import {parse as peggyParse} from './grammar'

export const NodeKindEnum = {
  Or: 'Or',
  And: 'And',
  Group: 'Group',
  Not: 'Not',
  Nothing: 'Nothing',
  Number: 'Number',
  Qualifier: 'Qualifier',
  Regex: 'Regex',
  Text: 'Text',
} as const

export type NodeKind = keyof typeof NodeKindEnum

export interface Location {
  start: number
  end: number
}

export interface BaseNode {
  kind: NodeKind
}

export interface NothingNode extends BaseNode {
  kind: 'Nothing'
}

export interface NodeWithLocation extends BaseNode {
  location: Location
}
export interface NodeWithChildren extends NodeWithLocation {
  children: FinalNode[]
}

export interface GroupNode extends NodeWithChildren {
  kind: 'Group'
}

export interface NodeWithOperatorLocations extends BaseNode {
  operatorLocations: Location[]
}
export interface AndNode extends NodeWithChildren, NodeWithOperatorLocations {
  kind: 'And'
}

export interface NotNode extends NodeWithChildren, NodeWithOperatorLocations {
  kind: 'Not'
}

export interface OrNode extends NodeWithChildren, NodeWithOperatorLocations {
  kind: 'Or'
}

export interface QualifierNode extends NodeWithLocation {
  kind: 'Qualifier'
  qualifier: string
  raw: string
  content: BaseNode
}

export function hasLocation(node: BaseNode): node is NodeWithLocation {
  return (node as NodeWithLocation).location !== undefined
}

export function isContentNode(node: BaseNode): node is ContentNode {
  return (node as ContentNode).value !== undefined
}

export type ContentNodeKind = Extract<NodeKind, 'Number' | 'Regex' | 'Text'>

export interface ContentNode extends NodeWithLocation {
  kind: ContentNodeKind
  value: string | number
}

export interface TextNode extends ContentNode {
  kind: 'Text'
  value: string
}

export interface NumberNode extends ContentNode {
  kind: 'Number'
  value: number
}

export interface RegexNode extends ContentNode {
  kind: 'Regex'
  value: string
}

export type FinalNode = AndNode | GroupNode | NothingNode | NumberNode | OrNode | QualifierNode | RegexNode | TextNode

export function parse(input: string): FinalNode {
  const raw = peggyParse(input)

  return raw as FinalNode
}

export const SearchType = {
  Unknown: '',
  Code: 'code',
  PRs: 'pr',
  Issues: 'issue',
  Discussions: 'discussion',
  Repositories: 'repo',
  Commits: 'commit',
  Packages: 'package',
  Marketplace: 'marketplace',
  Topics: 'topic',
  Users: 'user',
  Orgs: 'org',
  Wikis: 'wiki',
  CodeLegacy: 'codelegacy',
  AIResources: 'ai_resources',
} as const

export type SearchType = (typeof SearchType)[keyof typeof SearchType]

function scopedToType(type: SearchType, allowed: SearchType[]): boolean {
  if (type === SearchType.Unknown) return true
  return allowed.includes(type)
}

export function isQualifierCompatible(type: SearchType, qualifierKind: string): boolean {
  const compatible = getCompatibleQualifiers(type)
  return compatible[qualifierKind] || false
}

export function getCompatibleQualifiers(type: SearchType): Record<string, boolean> {
  return {
    [SearchType.Unknown]: {},
    [SearchType.AIResources]: {},
    [SearchType.Code]: {
      Language: true,
      Repo: true,
      Org: true,
      Enterprise: true,
      Symbol: true,
      Is: true,
      Saved: true,
      Path: true,
      Content: true,
      Trait: true,
      Type: true,
    },
    [SearchType.PRs]: {
      In: true,
      Sort: true,
      Author: true,
      Assignee: true,
      Mentions: true,
      Commenter: true,
      Involves: true,
      Milestone: true,
      Label: true,
      Language: true,
      Team: true,
      User: true,
      Repo: true,
      Org: true,
      Created: true,
      Updated: true,
      Merged: true,
      Closed: true,
      Comments: true,
      Type: true,
      State: true,
      Is: true,
      No: true,
      Status: true,
      Head: true,
      Base: true,
      Reactions: true,
      Interactions: true,
      Project: true,
      Review: true,
      Archived: true,
      Draft: true,
      Linked: true,
      Saved: true,
    },
    [SearchType.Discussions]: {
      Type: true,
      In: true,
      Sort: true,
      Author: true,
      Commenter: true,
      Involves: true,
      User: true,
      Org: true,
      Repo: true,
      Created: true,
      Updated: true,
      Comments: true,
      Is: true,
      Category: true,
      Label: true,
      No: true,
      Saved: true,
    },
    [SearchType.Issues]: {
      In: true,
      Sort: true,
      Author: true,
      Assignee: true,
      Mentions: true,
      Commenter: true,
      Involves: true,
      Milestone: true,
      Label: true,
      Language: true,
      Team: true,
      User: true,
      Repo: true,
      Org: true,
      Created: true,
      Updated: true,
      Closed: true,
      Comments: true,
      Type: true,
      State: true,
      Is: true,
      No: true,
      Status: true,
      Reactions: true,
      Interactions: true,
      Project: true,
      Linked: true,
      Saved: true,
      Reason: true,
    },
    [SearchType.Repositories]: {
      In: true,
      Sort: true,
      Type: true,
      Size: true,
      Forks: true,
      Fork: true,
      Pushed: true,
      Org: true,
      User: true,
      Repo: true,
      Language: true,
      License: true,
      Created: true,
      Followers: true,
      Stars: true,
      Mirror: true,
      Template: true,
      Is: true,
      Topic: true,
      Archived: true,
      Has: true,
      Saved: true,
    },
    [SearchType.Users]: {
      In: true,
      FullName: true,
      Language: true,
      Location: true,
      User: true,
      Org: true,
      Created: true,
      Followers: true,
      Type: true,
      Is: true,
      Saved: true,
    },
    [SearchType.Orgs]: {
      In: true,
      FullName: true,
      Language: true,
      Location: true,
      User: true,
      Org: true,
      Created: true,
      Followers: true,
      Type: true,
      Is: true,
      Saved: true,
    },
    [SearchType.Wikis]: {
      Updated: true,
      Org: true,
      Repo: true,
      Sort: true,
      In: true,
      Type: true,
      Saved: true,
    },
    [SearchType.Topics]: {
      Name: true,
      Created: true,
      Is: true,
      Repositories: true,
      Type: true,
      Saved: true,
    },
    [SearchType.Commits]: {
      Is: true,
      Merge: true,
      Sort: true,
      Author: true,
      User: true,
      Org: true,
      Repo: true,
      Type: true,
      Saved: true,
      Sha: true,
      Tree: true,
    },
    [SearchType.Packages]: {
      Name: true,
      Sort: true,
      User: true,
      Org: true,
      Repo: true,
      Topic: true,
      Type: true,
      Saved: true,
    },
    [SearchType.Marketplace]: {
      In: true,
      InputModality: true,
      InputTokens: true,
      Is: true,
      Language: true,
      License: true,
      OutputModality: true,
      OutputTokens: true,
      Publisher: true,
      Category: true,
      Name: true,
      RateLimitTier: true,
      Sort: true,
      State: true,
      Task: true,
      Type: true,
      Saved: true,
    },
    [SearchType.CodeLegacy]: {
      Language: true,
      Repo: true,
      Org: true,
      Fork: true,
      Symbol: true,
      Filename: true,
      Path: true,
      Size: true,
      Saved: true,
    },
  }[type]
}

// NOTE: this is generated from the getCompatibleQualifiers mapping above! To
// regenerate it, see the 'check/generate qualifierTypeMapping' test in blackbird.test.ts
const qualifierTypeMapping: Record<string, SearchType[]> = {
  Language: [
    SearchType.Code,
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Repositories,
    SearchType.Marketplace,
    SearchType.Users,
    SearchType.Orgs,
  ],
  Repo: [
    SearchType.Code,
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Commits,
    SearchType.Packages,
    SearchType.Wikis,
  ],
  Org: [
    SearchType.Code,
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Commits,
    SearchType.Packages,
    SearchType.Users,
    SearchType.Orgs,
    SearchType.Wikis,
  ],
  Enterprise: [SearchType.Code],
  Symbol: [SearchType.Code],
  Is: [
    SearchType.Code,
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Commits,
    SearchType.Marketplace,
    SearchType.Topics,
    SearchType.Users,
    SearchType.Orgs,
  ],
  Saved: [
    SearchType.Code,
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Commits,
    SearchType.Packages,
    SearchType.Marketplace,
    SearchType.Topics,
    SearchType.Users,
    SearchType.Orgs,
    SearchType.Wikis,
  ],
  Path: [SearchType.Code],
  Content: [SearchType.Code],
  Trait: [SearchType.Code],
  Type: [
    SearchType.Code,
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Commits,
    SearchType.Packages,
    SearchType.Marketplace,
    SearchType.Topics,
    SearchType.Users,
    SearchType.Orgs,
    SearchType.Wikis,
  ],
  In: [
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Marketplace,
    SearchType.Users,
    SearchType.Orgs,
    SearchType.Wikis,
  ],
  Sort: [
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Commits,
    SearchType.Packages,
    SearchType.Marketplace,
    SearchType.Wikis,
  ],
  Author: [SearchType.PRs, SearchType.Issues, SearchType.Discussions, SearchType.Commits],
  Assignee: [SearchType.PRs, SearchType.Issues],
  Mentions: [SearchType.PRs, SearchType.Issues],
  Commenter: [SearchType.PRs, SearchType.Issues, SearchType.Discussions],
  Involves: [SearchType.PRs, SearchType.Issues, SearchType.Discussions],
  Milestone: [SearchType.PRs, SearchType.Issues],
  Label: [SearchType.PRs, SearchType.Issues, SearchType.Discussions],
  Team: [SearchType.PRs, SearchType.Issues],
  User: [
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Commits,
    SearchType.Packages,
    SearchType.Users,
    SearchType.Orgs,
  ],
  Created: [
    SearchType.PRs,
    SearchType.Issues,
    SearchType.Discussions,
    SearchType.Repositories,
    SearchType.Topics,
    SearchType.Users,
    SearchType.Orgs,
  ],
  Updated: [SearchType.PRs, SearchType.Issues, SearchType.Discussions, SearchType.Wikis],
  Merged: [SearchType.PRs],
  Closed: [SearchType.PRs, SearchType.Issues],
  Comments: [SearchType.PRs, SearchType.Issues, SearchType.Discussions],
  State: [SearchType.PRs, SearchType.Issues, SearchType.Marketplace],
  No: [SearchType.PRs, SearchType.Issues, SearchType.Discussions],
  Status: [SearchType.PRs, SearchType.Issues],
  Head: [SearchType.PRs],
  Base: [SearchType.PRs],
  Reactions: [SearchType.PRs, SearchType.Issues],
  Interactions: [SearchType.PRs, SearchType.Issues],
  Project: [SearchType.PRs, SearchType.Issues],
  Review: [SearchType.PRs],
  Archived: [SearchType.PRs, SearchType.Repositories],
  Draft: [SearchType.PRs],
  Linked: [SearchType.PRs, SearchType.Issues],
  Reason: [SearchType.Issues],
  Category: [SearchType.Discussions, SearchType.Marketplace],
  Size: [SearchType.Repositories],
  Forks: [SearchType.Repositories],
  Fork: [SearchType.Repositories],
  Pushed: [SearchType.Repositories],
  License: [SearchType.Repositories, SearchType.Marketplace],
  Followers: [SearchType.Repositories, SearchType.Users, SearchType.Orgs],
  Stars: [SearchType.Repositories],
  Mirror: [SearchType.Repositories],
  Template: [SearchType.Repositories],
  Topic: [SearchType.Repositories, SearchType.Packages],
  Has: [SearchType.Repositories],
  Merge: [SearchType.Commits],
  Sha: [SearchType.Commits],
  Tree: [SearchType.Commits],
  Name: [SearchType.Packages, SearchType.Marketplace, SearchType.Topics],
  InputModality: [SearchType.Marketplace],
  InputTokens: [SearchType.Marketplace],
  OutputModality: [SearchType.Marketplace],
  OutputTokens: [SearchType.Marketplace],
  Publisher: [SearchType.Marketplace],
  RateLimitTier: [SearchType.Marketplace],
  Task: [SearchType.Marketplace],
  Repositories: [SearchType.Topics],
  FullName: [SearchType.Users, SearchType.Orgs],
  Location: [SearchType.Users, SearchType.Orgs],
}

export function getCompatibleSearchTypes(qualifierKind: string): SearchType[] {
  return qualifierTypeMapping[qualifierKind] || []
}

// Returns a set of possible fixed values for the qualifier (or empty, if there are none).
// The type field is the search type, i.e. code, issues, prs, discussions, topics, etc.
// If the type field is blank, then don't apply any restrictions based on type.
export function getPossibleQualifierValues(type: SearchType, qualifierKind: string, today?: Date) {
  if (!today) {
    today = new Date()
  }

  if (qualifierKind === 'Sort') {
    // Sort isn't supported in code search
    if (type === SearchType.Code) return []

    if (type === SearchType.Marketplace) {
      return ['created-asc', 'created-desc', 'name-asc', 'name-desc', 'popularity', 'updated-desc', 'updated-asc']
    }

    return [
      'interactions',
      'interactions-asc',
      'reactions',
      'reactions-asc',
      'author-date',
      'author-date-asc',
      'committer-date',
      'committer-date-asc',
      'updated',
      'updated-asc',
    ]
  } else if (qualifierKind === 'No') {
    if (scopedToType(type, [SearchType.Issues, SearchType.PRs, SearchType.Discussions])) {
      return ['label', 'assignee', 'project', 'milestone']
    }
  } else if (qualifierKind === 'Type') {
    return ['user', 'org', 'pr', 'issue', 'discussion', 'marketplace', 'package', 'commit', 'topic', 'wiki']
  } else if (qualifierKind === 'Status') {
    if (scopedToType(type, [SearchType.PRs])) {
      return ['pending', 'failure', 'success']
    }
  } else if (qualifierKind === 'Archived' || qualifierKind === 'Draft') {
    if (scopedToType(type, [SearchType.PRs])) {
      return ['true', 'false']
    }
  } else if (qualifierKind === 'Linked') {
    if (scopedToType(type, [SearchType.Issues, SearchType.PRs])) {
      return ['pr', 'issue']
    }
  } else if (qualifierKind === 'Has') {
    if (scopedToType(type, [SearchType.Repositories])) {
      return ['funding-file']
    }
  } else if (qualifierKind === 'Is') {
    if (scopedToType(type, [SearchType.Code])) {
      return ['archived', 'fork']
    } else if (scopedToType(type, [SearchType.Users])) {
      return ['sponsorable']
    } else if (scopedToType(type, [SearchType.Issues])) {
      return ['open', 'closed', 'public', 'private']
    } else if (scopedToType(type, [SearchType.PRs])) {
      return ['open', 'closed', 'public', 'private', 'merged', 'unmerged']
    } else if (
      scopedToType(type, [SearchType.Discussions, SearchType.Repositories, SearchType.Commits, SearchType.Packages])
    ) {
      return ['public', 'private', 'issue', 'pr']
    }
  } else if (qualifierKind === 'State') {
    if (scopedToType(type, [SearchType.Issues, SearchType.PRs])) {
      return ['open', 'closed']
    }
  } else if (qualifierKind === 'Review') {
    if (scopedToType(type, [SearchType.PRs])) {
      return ['none', 'required', 'approved', 'changes_requested']
    }
  } else if (qualifierKind === 'Stars') {
    return ['0..100', '200', '<200', '>500', '>1000', '>2000', '>4000']
  } else if (qualifierKind === 'Forks') {
    return ['<5', '50..100', '200', '>500']
  } else if (qualifierKind === 'Reason') {
    return ['completed', 'not planned', 'reopened']
  } else if (qualifierKind === 'Location') {
    return [
      'San Francisco, CA',
      'Bellevue, WA',
      'Brooklyn, NY',
      'Oxford, United Kingdom',
      'Amsterdam, Netherlands',
      'Copenhagen, Denmark',
      'Hyderabad, India',
      'Melbourne, Australia',
      'Tokyo, Japan',
      'Sydney, Australia',
    ]
  } else if (qualifierKind === 'License') {
    if (type === SearchType.Marketplace) {
      return ['mit', 'custom']
    }
    return ['0bsd', 'cc', 'gpl', 'lgpl', 'apache-2.0']
  } else if (qualifierKind === 'Created' || qualifierKind === 'Updated' || qualifierKind === 'Pushed') {
    const formatDate = (d: Date) =>
      `${d.getFullYear()}-${(d.getMonth() + 1).toString().padStart(2, '0')}-${d.getDate().toString().padStart(2, '0')}`

    const startOfWeek = new Date(today.getTime() - today.getDay() * 24 * 3600 * 1000)
    const startOfMonth = new Date(today.getTime())
    startOfMonth.setDate(1)
    const startOfYear = new Date(today.getTime())
    startOfYear.setDate(1)
    startOfYear.setMonth(0)

    return [
      formatDate(today),
      `>${formatDate(startOfWeek)}`,
      `>${formatDate(startOfMonth)}`,
      `>${formatDate(startOfYear)}`,
    ]
  }
  return []
}
